import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import { CostCompetitiveIndex } from "../../../types/contractDashboard";
import styles from "./costCompetitivenessIndex.module.scss";

interface CostCompetitivenessIndexProps {
  data: CostCompetitiveIndex[];
}

const CostCompetitivenessIndex: React.FC<CostCompetitivenessIndexProps> = ({
  data,
}) => {
  const chartData = useMemo(() => {
    return data.map((supplier) => ({
      name: supplier.supplier_name,
      score: Math.round(supplier.supplier_price_value),
    }));
  }, [data]);

  const marketAverage = 100;

  // Custom tooltip to show better context
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const score = payload[0].value;
      const isAboveMarket = score > marketAverage;
      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 12px",
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#111827", fontWeight: 600, mb: 0.5 }}
          >
            {payload[0].payload.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isAboveMarket ? "#ef4444" : "#10b981",
              fontWeight: 500,
            }}
          >
            Score: {score}
          </Typography>
          <Typography variant="caption" sx={{ color: "#6b7280" }}>
            {isAboveMarket ? "Above Market" : "Below Market"}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <Typography variant="h6" className={styles.title}>
            Cost Competitiveness Index
          </Typography>
          <Typography variant="h6" sx={{ color: "#6b7280", fontSize: "16px", fontWeight: 600 }}>
            Market Avg: 100
          </Typography>
        </Box>
        <Typography variant="body2" className={styles.subtitle}>
          Supplier price vs market benchmark (100 = market average).
        </Typography>
      </Box>

      <Box className={styles.chartContainer} sx={{ position: "relative" }}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
            barCategoryGap="15%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={{ stroke: "#d1d5db" }}
              tickLine={false}
              domain={[0, "dataMax + 10"]}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: "#374151", fontSize: 11, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0, 0, 0, 0.03)" }} />
            <ReferenceLine
              x={marketAverage}
              stroke="#6b7280"
              strokeDasharray="5 5"
              strokeWidth={1.5}
            />
            <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={28}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.score > marketAverage ? "#ef4444" : "#10b981"}
                  opacity={0.9}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box className={styles.legend}>
        <Box className={styles.legendItem}>
          <Box className={styles.legendColorGreen} />
          <Typography variant="body2" className={styles.legendLabel}>
            Below Market (Better)
          </Typography>
        </Box>
        <Box className={styles.legendItem}>
          <Box className={styles.legendColorRed} />
          <Typography variant="body2" className={styles.legendLabel}>
            Above Market (Higher)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CostCompetitivenessIndex;

